import { Box, Container, Grid, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { FC, useState } from 'react';
import 'react-multi-carousel/lib/styles.css';
import Image from 'blog/components/Image';
import InfoPopup from './InfoPopup';
import MoreButton from 'components/atoms/MoreButton';

export interface InfoSlideProps {
  image: string;
  title: string;
  text: string;
  popupContent?: { name: string; position: string; description: string };
  project?: string;
}

interface InfoSliderProps {
  list: Array<InfoSlideProps>;
  small?: boolean;
  blockTitle?: string;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    imageWrapper: {
      position: 'relative',
      '& > div.hover': {
        height: 0,
      },
      [theme.breakpoints.up('sm')]: {
        '& > div.hover': {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '99%',
          background: 'rgba(224, 98, 30, 0.7)',
          zIndex: 10,
          display: 'none',
        },
        '&:hover > div.hover': {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    },
    title: {
      color: '#292C3D',
      fontSize: 15,
      fontWeight: 500,
      [theme.breakpoints.up('md')]: {
        fontSize: 25,
      },
    },
    text: {
      color: theme.palette.primary.main,
      fontSize: 10,
      fontWeight: 500,
      [theme.breakpoints.up('md')]: {
        fontSize: 15,
      },
    },
    projectTitle: {
      color: '#4F5260',
      fontWeight: 600,
      fontSize: 15,
      opacity: 0.5,
      marginBottom: 10,
      [theme.breakpoints.up('md')]: {
        fontSize: 25,
        marginBottom: theme.spacing(4),
      },
    },
  });
});

const InfoSlider: FC<InfoSliderProps> = ({ list, small = false, blockTitle }) => {
  const [selectedItem, setSelectedItem] = useState<InfoSlideProps>(null);
  const classes = useStyles();
  const isSmallUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const onClosePopup = () => {
    setSelectedItem(null);
  };

  const onSelect = (item: InfoSlideProps) => {
    setSelectedItem(item);
  };

  if (small) {
    return (
      <Container>
        <Grid container spacing={5}>
          {list.map((item, index) => {
            const { title, image } = item;

            return (
              <Grid item xs={12} sm={3} md={2} key={index}>
                <Box className={classes.imageWrapper}>
                  <Image src={image} alt={title} width={330} height={450} />
                </Box>
                <Box mt={2} />
                <Typography variant="h4" style={{ fontSize: 20 }}>
                  {title}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Grid container spacing={isSmallUp ? 4 : 2}>
          {list.map((item, index) => {
            const { title, text, image, popupContent, project } = item;
            return (
              <Grid
                item
                xs={6}
                sm={6}
                md={4}
                lg={3}
                key={index}
                onClick={() => popupContent && onSelect(item)}
                style={popupContent ? { cursor: 'pointer' } : {}}
              >
                <Box display="flex" flexDirection="column" height="100%">
                  {/* Equal height project block */}
                  {blockTitle !== 'Leadership' && (
                    <Box minHeight={14}>
                      {project ? (
                        <Typography className={classes.projectTitle}>{project.toUpperCase()}</Typography>
                      ) : (
                        <Typography className={classes.projectTitle} style={{ visibility: 'hidden' }}>
                          {'PLACEHOLDER'}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Box className={classes.imageWrapper}>
                    <Image src={image} alt={title} width={330} height={450} />
                    {popupContent && isSmallUp && (
                      <div className={'hover'}>
                        <MoreButton onClick={() => popupContent && onSelect(item)} />
                      </div>
                    )}
                  </Box>
                  <Box mt={2} />
                  <Typography className={classes.title}>{title}</Typography>
                  <Box mt={1} />
                  <Typography className={classes.text}>{text}</Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>
      <InfoPopup open={selectedItem !== null} item={selectedItem} onClose={onClosePopup} />
    </>
  );
};

export default InfoSlider;
